/**
 * Organization header block for documents — one fetch that yields everything a
 * document needs about the issuing org: display identity, business identity,
 * tax identity, logo, plus the two `meta` inputs (base currency, timezone).
 *
 * Mirrors, in one server-callable function, what the screen assembles from
 * three hooks: `OrganizationContext` (name/country/logo/currency/timezone),
 * `useOrganizationBusinessIdentity` (+ its `legal_forms` join) and
 * `useFullTaxSettings`.
 */
import { EMPTY_ORG_BUSINESS_IDENTITY, } from "./common.js";
import { resolveTaxJurisdictionFrom, resolveTaxName } from "./taxIdentity.js";
/** Storage bucket holding org logos (same bucket the web `useSignedUrl` reads). */
const LOGO_BUCKET = "files";
const BASE64_ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";
/**
 * Base64 without `Buffer` or `btoa` — both are host globals (Node-only and
 * DOM-only respectively) and this package must stay isomorphic.
 */
function bytesToBase64(bytes) {
    let out = "";
    for (let i = 0; i < bytes.length; i += 3) {
        const hasB1 = i + 1 < bytes.length;
        const hasB2 = i + 2 < bytes.length;
        const b0 = bytes[i];
        const b1 = hasB1 ? bytes[i + 1] : 0;
        const b2 = hasB2 ? bytes[i + 2] : 0;
        out += BASE64_ALPHABET[b0 >> 2];
        out += BASE64_ALPHABET[((b0 & 0x03) << 4) | (b1 >> 4)];
        out += hasB1 ? BASE64_ALPHABET[((b1 & 0x0f) << 2) | (b2 >> 6)] : "=";
        out += hasB2 ? BASE64_ALPHABET[b2 & 0x3f] : "=";
    }
    return out;
}
const MIME_BY_EXTENSION = {
    png: "image/png",
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    gif: "image/gif",
    webp: "image/webp",
    svg: "image/svg+xml",
};
/** The image types a document may inline — the values above, deduplicated. */
const ALLOWED_LOGO_MIME_TYPES = new Set(Object.values(MIME_BY_EXTENSION));
/**
 * Biggest logo we will inline, in bytes.
 *
 * The bytes do not just travel — they are base64'd (+33%) into the HTML
 * document string, held in memory by the API, parsed by Chromium and decoded
 * into a bitmap. An org that uploaded a 30MB "logo" would spend the render
 * budget on it and could take the shared browser's memory with it. A logo that
 * does not fit is dropped, like any other logo failure: the document prints.
 */
const MAX_LOGO_BYTES = 1_000_000;
/**
 * The MIME type to stamp on the `data:` URI, or `null` if this is not an image
 * we inline.
 *
 * The storage-reported type is a stored attribute an uploader controls, and it
 * lands verbatim in a `data:` URI inside the document — so it is FILTERED
 * through the same allowlist that backs the extension fallback rather than
 * trusted. An unrecognized report falls back to the extension (storage does not
 * always report a type at all), and an unrecognized extension degrades to null.
 */
function contentTypeFor(blobType, storagePath) {
    // Strip any `; charset=…` parameter before matching, and compare lowercased.
    const reported = blobType?.split(";")[0]?.trim().toLowerCase();
    if (reported && ALLOWED_LOGO_MIME_TYPES.has(reported))
        return reported;
    const extension = storagePath.split(".").pop()?.toLowerCase() ?? "";
    return MIME_BY_EXTENSION[extension] ?? null;
}
/**
 * Download a logo from the private `files` bucket and inline it.
 *
 * NEVER throws: a missing object, an RLS denial on the bucket, a file too big
 * to inline, a type we do not recognize, or a transport failure all degrade to
 * `null` (the document renders without a logo). A PDF must not 500 because of a
 * decorative asset.
 */
async function downloadLogoDataUri(client, storagePath) {
    try {
        const { data, error } = await client.storage.from(LOGO_BUCKET).download(storagePath);
        if (error || !data)
            return null;
        const bytes = new Uint8Array(await data.arrayBuffer());
        if (bytes.length === 0 || bytes.length > MAX_LOGO_BYTES)
            return null;
        const mimeType = contentTypeFor(data.type, storagePath);
        if (!mimeType)
            return null;
        return `data:${mimeType};base64,${bytesToBase64(bytes)}`;
    }
    catch {
        return null;
    }
}
/** PostgREST returns a to-one embed as an object, but tolerate an array shape. */
function firstLegalForm(embed) {
    const row = Array.isArray(embed) ? embed[0] : embed;
    if (!row || typeof row !== "object")
        return null;
    const { code, name, country_code } = row;
    if (typeof code !== "string" || typeof name !== "string")
        return null;
    return {
        code,
        name,
        country_code: typeof country_code === "string" ? country_code : null,
    };
}
const BUSINESS_IDENTITY_SELECT = `
  registration_number,
  tax_entity_number,
  website_url,
  contact_email,
  registered_address,
  postal_address,
  iban,
  bic,
  is_tax_exempt,
  legal_forms (
    code,
    name,
    country_code
  )
`;
/**
 * Fetch the org identity block plus the org-level `meta` inputs.
 *
 * Throws when the `organizations` row can't be read (not found, or RLS denial)
 * — callers map that to a 404. The business-identity and tax-settings rows are
 * optional setup: a missing row yields nulls, not an error.
 */
export async function fetchOrgContext(client, orgId, opts) {
    const [orgResult, identityResult, taxSettingsResult] = await Promise.all([
        client
            .from("organizations")
            .select("name, currency, country, timezone, logo_medium, logo_large, show_account_numbers")
            .eq("id", orgId)
            .maybeSingle(),
        client
            .from("organization_business_identity")
            .select(BUSINESS_IDENTITY_SELECT)
            .eq("organization_id", orgId)
            .maybeSingle(),
        client
            .from("organization_tax_settings")
            .select("primary_tax_jurisdiction, tax_identification_number, fiscal_year_start_month")
            .eq("organization_id", orgId)
            .maybeSingle(),
    ]);
    if (orgResult.error)
        throw orgResult.error;
    if (!orgResult.data)
        throw new Error(`Organization ${orgId} not found`);
    if (identityResult.error)
        throw identityResult.error;
    if (taxSettingsResult.error)
        throw taxSettingsResult.error;
    const org = orgResult.data;
    const identityRow = identityResult.data;
    const taxSettings = taxSettingsResult.data;
    const identity = identityRow
        ? {
            registration_number: identityRow.registration_number,
            tax_entity_number: identityRow.tax_entity_number,
            website_url: identityRow.website_url,
            contact_email: identityRow.contact_email,
            registered_address: identityRow.registered_address,
            postal_address: identityRow.postal_address,
            iban: identityRow.iban,
            bic: identityRow.bic,
            is_tax_exempt: identityRow.is_tax_exempt ?? false,
            legalForm: firstLegalForm(identityRow.legal_forms),
        }
        : { ...EMPTY_ORG_BUSINESS_IDENTITY };
    // Same precedence as ReportHeader (`logo_medium || logo_large`), flippable
    // for documents that print the logo large.
    const storagePath = (opts.logoSize === "large"
        ? org.logo_large || org.logo_medium
        : org.logo_medium || org.logo_large) || null;
    const dataUri = opts.inlineLogo && storagePath ? await downloadLogoDataUri(client, storagePath) : null;
    const taxJurisdiction = resolveTaxJurisdictionFrom(taxSettings?.primary_tax_jurisdiction, org.country);
    return {
        identity: {
            name: org.name,
            country: org.country,
            logo: { storagePath, dataUri },
            identity,
            taxIdentificationNumber: taxSettings?.tax_identification_number ?? null,
            taxJurisdiction,
            taxName: resolveTaxName(taxJurisdiction),
        },
        baseCurrency: org.currency,
        orgTimezone: org.timezone,
        showAccountNumbers: org.show_account_numbers ?? true,
        fiscalYearStartMonth: taxSettings?.fiscal_year_start_month ?? null,
    };
}
/**
 * Identity-block-only convenience wrapper over `fetchOrgContext` for callers
 * that already know the org's currency and timezone.
 */
export async function fetchOrgIdentityBlock(client, orgId, opts) {
    const context = await fetchOrgContext(client, orgId, opts);
    return context.identity;
}
//# sourceMappingURL=orgIdentity.js.map